import {
  useGetPersonalizedProductsQuery,
  useGetSponsoredBrandQuery,
  PersonalizedProductsModelNames,
} from 'types/graphql';
import { NetworkStatus } from 'apollo-client';
import { useAdsEnabledForCategoryPage } from 'src/utils/ads/hooks/use-ads-enabled';
import { Category } from 'src/generics/data/generic-category/data.types';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { useAdInventoryId, ADS_FETCH_POLICY } from 'src/utils/ads/helpers';

import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useCart from 'src/hooks/use-cart';
import { useEnabledForCategoryPage as usePersonalizationEnabledForCategoryPage } from 'src/utils/personalization/hooks/use-personalization-enabled';
import { usePersonalizedItemsV2 } from 'src/recommendations/use-personalized-items-v2';
import { useFiltersForSponsoredResources } from './use-filters-for-sponsored-resources';

type CategoryProductsCarouselQueryArgs = {
  category?: Category | null;
  skipApiCall?: boolean;
};

export type CategoryProductsCarouselsQueries = {
  loading: boolean;
  sponsoredBrandQuery: ReturnType<typeof useGetSponsoredBrandQuery>;
  personalizedProductsQuery: ReturnType<typeof useGetPersonalizedProductsQuery>;
};

function useCategoryProductsForCarouselsQueriesV2({
  category,
  skipApiCall = false,
}: CategoryProductsCarouselQueryArgs): CategoryProductsCarouselsQueries {
  const { dispensary } = useDispensary();

  const dispensaryId = dispensary?.id;
  const isReady = !!dispensaryId;
  const inventoryId = useAdInventoryId(dispensaryId, category);
  const isPersonalizationEnabledForCategoryPage = usePersonalizationEnabledForCategoryPage();
  const adsEnabledForCategoryPage = useAdsEnabledForCategoryPage();

  const skipAll = !isReady || !category;
  const skipAdQuery = skipAll || !adsEnabledForCategoryPage;
  const skipPersonalizationQuery = skipApiCall || skipAll || !isPersonalizationEnabledForCategoryPage;
  const { filters } = useFiltersForSponsoredResources();

  const sponsoredBrandQuery = useGetSponsoredBrandQuery({
    fetchPolicy: ADS_FETCH_POLICY,
    skip: skipAdQuery,
    variables: {
      input: {
        dispensaryId,
        category,
        inventoryId,
        minimumProducts: 3,
        filters,
      },
    },
  });

  const { data, loading, error } = usePersonalizedItemsV2(
    () => Promise.resolve([]),
    [], // No product context
    PersonalizedProductsModelNames.recommendedForYou,
    {
      limit: 25,
      skip: skipPersonalizationQuery,
      category: category ?? undefined,
    }
  );

  // Transform V2 data to match V1 interface for backward compatibility
  const transformedData = data
    ? {
        getPersonalizedProducts: {
          products: data,
        },
      }
    : undefined;

  const personalizedProductsQuery = ({
    data: transformedData,
    loading,
    error: error as any, // V2 returns Error, V1 expects ApolloError - cast for compatibility
    // Add required properties to match GraphQL query result interface
    refetch: () => {
      const currentTransformedData = data
        ? {
            getPersonalizedProducts: {
              products: data,
            },
          }
        : undefined;
      return Promise.resolve({
        data: currentTransformedData,
        loading: false,
        networkStatus: NetworkStatus.ready,
        stale: false,
      });
    },
    fetchMore: () => {
      const currentTransformedData = data
        ? {
            getPersonalizedProducts: {
              products: data,
            },
          }
        : undefined;
      return Promise.resolve({
        data: currentTransformedData,
        loading: false,
        networkStatus: NetworkStatus.ready,
        stale: false,
      });
    },
    updateQuery: () =>
      data
        ? {
            getPersonalizedProducts: {
              products: data,
            },
          }
        : undefined,
    startPolling: undefined,
    stopPolling: undefined,
    subscribeToMore: undefined,
    client: {} as any,
    networkStatus: NetworkStatus.ready,
    called: true,
    variables: {},
  } as unknown) as ReturnType<typeof useGetPersonalizedProductsQuery>;

  return {
    loading: !isReady || sponsoredBrandQuery.loading || loading,
    sponsoredBrandQuery,
    personalizedProductsQuery,
  };
}

function useCategoryProductsForCarouselsQueriesV1({
  category,
  skipApiCall = false,
}: CategoryProductsCarouselQueryArgs): CategoryProductsCarouselsQueries {
  const { dispensary } = useDispensary();
  const { menuType } = useCart();

  const dispensaryId = dispensary?.id;
  const isReady = !!dispensaryId;
  const inventoryId = useAdInventoryId(dispensaryId, category);
  const isPersonalizationEnabledForCategoryPage = usePersonalizationEnabledForCategoryPage();
  const adsEnabledForCategoryPage = useAdsEnabledForCategoryPage();

  const skipAll = !isReady || !category;
  const skipAdQuery = skipAll || !adsEnabledForCategoryPage;
  const skipPersonalizationQuery = skipApiCall || skipAll || !isPersonalizationEnabledForCategoryPage;
  const { filters } = useFiltersForSponsoredResources();
  const sponsoredBrandQuery = useGetSponsoredBrandQuery({
    fetchPolicy: ADS_FETCH_POLICY,
    skip: skipAdQuery,
    variables: {
      input: {
        dispensaryId,
        category,
        inventoryId,
        minimumProducts: 3,
        filters,
      },
    },
  });

  const personalizedProductsQuery = useGetPersonalizedProductsQuery({
    fetchPolicy: 'network-only',
    skip: skipPersonalizationQuery,
    variables: {
      input: {
        dispensaryId,
        limit: 25,
        filter: {
          menuType,
          category,
        },
      },
    },
  });

  return {
    loading: !isReady || sponsoredBrandQuery.loading || personalizedProductsQuery.loading,
    sponsoredBrandQuery,
    personalizedProductsQuery,
  };
}

export function useCategoryProductsForCarouselsQueries({
  category,
}: CategoryProductsCarouselQueryArgs): CategoryProductsCarouselsQueries {
  const flags = useFlags();
  const isPersonalizedProductsV2Enabled = flags['growth.platform.personalization-recommended-v2.rollout'] ?? false;

  const v2Result = useCategoryProductsForCarouselsQueriesV2({
    category,
    skipApiCall: !isPersonalizedProductsV2Enabled,
  });
  const v1Result = useCategoryProductsForCarouselsQueriesV1({ category, skipApiCall: isPersonalizedProductsV2Enabled });

  return isPersonalizedProductsV2Enabled ? v2Result : v1Result;
}
